/**
 * shares-api — typed wrappers around the 9 sharing HTTP endpoints shipped by
 * `external-sharing` Phase 2. Single source of truth for every web call into
 * the sharing surface; consumed by `use-shares.ts` (Phase 3 Session 3.1) +
 * `<SendDealLinkModal>` (Session 3.2) + `<SentTab>` actions (Session 3.3) +
 * Phase 4-6 owner/recipient features.
 *
 * Endpoint topology (from apps/api/CLAUDE.md "External Sharing — server completion"):
 *   - `/api/shared/*` — public surface, optionalAuth (4 endpoints; 1 used here)
 *   - `/api/shares/*` — authed surface, requireAuth blanket (9 endpoints)
 *
 * Wire-shape contract: every endpoint returns the `sendSuccess` envelope
 * (`{success: true, data: T}`). `api.get` / `api.post` / etc. unwrap to `T`
 * via the request<T>() helper at lib/api.ts:160-205, so callers receive the
 * data directly. ApiError propagates 4xx/5xx with the structured `code` +
 * `details` shape from `sharing.errors.ts` (SHARE_NOT_FOUND,
 * SHARE_DUPLICATE_RECIPIENT, etc.) — callers switch on `err.code`.
 */
import { api } from './api';
import type {
  SharedLinkResponse,
  CreateShareBody,
  ShareForkBackBody,
  VersionGraphResponse,
  VersionDiffResponse,
  SandboxRecomputeRequest,
  SandboxRecomputeResponse,
} from '@arcanum-hq/shared-types';

// Re-export the wire types so consumers don't need a parallel import line.
export type {
  SharedLinkResponse,
  CreateShareBody,
  ShareForkBackBody,
  VersionGraphResponse,
  VersionDiffResponse,
  SandboxRecomputeRequest,
  SandboxRecomputeResponse,
};

// ============================================================================
// URL helpers
// ============================================================================

/**
 * Build the public deal-link URL for a share. Reads `VITE_APP_URL` first
 * (production deploys set this via Cloud Storage build env); falls back to
 * `window.location.origin` for local dev (matches the running Vite dev
 * server's origin without needing the env var). Trailing slash on
 * `VITE_APP_URL` is normalized.
 *
 * Consumed by `<SendDealLinkModal>` (success + collision copy-link panels)
 * and `<SentShareRow>` (per-row [Copy link] button) — extracted here so
 * both surfaces stay in sync.
 */
export function buildShareUrl(shareId: string): string {
  const fromEnv = import.meta.env.VITE_APP_URL;
  const base =
    typeof fromEnv === 'string' && fromEnv.length > 0
      ? fromEnv.replace(/\/$/, '')
      : typeof window !== 'undefined'
      ? window.location.origin
      : '';
  return `${base}/deal/${shareId}`;
}

export interface UnreadCountResponse {
  unreadCount: number;
}

export interface RevokeShareResponse {
  ok: true;
  alreadyRevoked: boolean;
}

export interface MarkReadResponse {
  ok: true;
}

export interface ForkShareResponse {
  conversationId: string;
}

// ============================================================================
// Envelope unwrap
// ============================================================================

/**
 * Every successful API response is wrapped in `{success: true, data: T}` by
 * the server's `sendSuccess()` helper. `api.get/post/etc.` from `lib/api.ts`
 * does NOT auto-unwrap — it returns the raw envelope. This helper is the
 * single point where sharing wrappers narrow to the inner shape.
 *
 * Mirrors the `useApprovalStatus` + `useCreateConversation` pattern (both
 * read `envelope.data` after a typed `api.get`/`api.post`). Catching this
 * via a named helper keeps every wrapper consistent + makes the unwrap
 * step obvious in code review.
 */
type Envelope<T> = { data: T };
function unwrap<T>(envelope: Envelope<T>): T {
  return envelope.data;
}

// ============================================================================
// Authed surface — `/api/shares/*` (requireAuth blanket)
// ============================================================================

/**
 * `GET /api/shares?role=sent|received` — list shares for the caller, by role.
 * `sent` returns shares the caller created; `received` returns shares
 * `recipient_user_id = auth.uid()` AND `status = 'active'`.
 */
export function listShares(
  role: 'sent' | 'received'
): Promise<SharedLinkResponse[]> {
  return api
    .get<Envelope<SharedLinkResponse[]>>(`/api/shares?role=${role}`)
    .then(unwrap);
}

/**
 * `GET /api/shares/unread-count` — per-recipient unread signal: count of
 * `recipient_user_id = auth.uid() AND status = 'active' AND
 * recipient_first_viewed_at IS NULL`. Distinct from `view_count` (which
 * counts all viewers). Drives `<HeaderShareBell>` badge.
 */
export function getUnreadCount(): Promise<UnreadCountResponse> {
  return api
    .get<Envelope<UnreadCountResponse>>('/api/shares/unread-count')
    .then(unwrap);
}

/**
 * `POST /api/shares` — create a new share. 409 `SHARE_ALREADY_PENDING_OR_ACTIVE`
 * surfaces as `ApiError` with `details.existingShareId` so the caller can route
 * to the existing share (collision UX in `<SendDealLinkModal>`, Session 3.2).
 * Verified against the actual wire response 2026-05-09 walkthrough: server
 * factory uses `SHARE_ALREADY_PENDING_OR_ACTIVE`, NOT the spec's planned
 * `SHARE_DUPLICATE_RECIPIENT` alias.
 */
export function createShare(
  body: CreateShareBody
): Promise<SharedLinkResponse> {
  return api
    .post<Envelope<SharedLinkResponse>>('/api/shares', body)
    .then(unwrap);
}

/**
 * `PATCH /api/shares/:shareId` body `{status: 'revoked'}` — owner-only revoke.
 * Idempotent: already-revoked returns `{ok: true, alreadyRevoked: true}`.
 * Existing forks of the share survive (per spec §4 — fork-survives-revoke
 * invariant). Realtime `share_link_revoked` echo fans out to recipient's
 * `user:<userId>:shares` channel.
 */
export function revokeShare(shareId: string): Promise<RevokeShareResponse> {
  return api
    .patch<Envelope<RevokeShareResponse>>(`/api/shares/${shareId}`, {
      status: 'revoked',
    })
    .then(unwrap);
}

/**
 * `POST /api/shares/:shareId/mark-read` — recipient-side. Sets
 * `recipient_first_viewed_at = now()` if NULL (idempotent on repeat reads);
 * always bumps `view_count` + `last_viewed_at`. Triggered by Received-tab
 * mount (Phase 4 Session 4.1) + share-detail navigation.
 */
export function markRead(shareId: string): Promise<MarkReadResponse> {
  return api
    .post<Envelope<MarkReadResponse>>(`/api/shares/${shareId}/mark-read`, {})
    .then(unwrap);
}

/**
 * `POST /api/shares/:shareId/fork` — recipient creates a new conversation
 * forked from the share's `frozen_state`. Returns the new conversationId
 * for redirect to `/builder?conversation=<id>`. (Phase 4 Session 4.2.)
 */
export function forkShare(shareId: string): Promise<ForkShareResponse> {
  return api
    .post<Envelope<ForkShareResponse>>(`/api/shares/${shareId}/fork`, {})
    .then(unwrap);
}

/**
 * `POST /api/shares/fork-back` — recipient-initiated share back to original
 * owner. Body carries `forkConversationId` + optional `message`. Service
 * walks the fork chain + emits `SHARE_FORK_RETURNED` lifecycle event.
 * (Phase 4 Session 4.3.)
 */
export function shareForkBack(
  body: ShareForkBackBody
): Promise<SharedLinkResponse> {
  return api
    .post<Envelope<SharedLinkResponse>>('/api/shares/fork-back', body)
    .then(unwrap);
}

/**
 * `GET /api/shares/:conversationId/version-graph` — DAG of all shares +
 * forks descending from this conversation. Owner OR recipient access
 * required. (Phase 6 Session 6.1.)
 */
export function getVersionGraph(
  conversationId: string
): Promise<VersionGraphResponse> {
  return api
    .get<Envelope<VersionGraphResponse>>(
      `/api/shares/${conversationId}/version-graph`
    )
    .then(unwrap);
}

/**
 * `GET /api/shares/diff?from=<convId>&to=<convId>` — input + metric diff
 * between two conversations the caller has read access to. Returns
 * `VersionDiffResponse` with `inputChanges[]` + `metricChanges[]`. (Phase 6
 * Session 6.2.)
 */
export function getDiff(
  from: string,
  to: string
): Promise<VersionDiffResponse> {
  const qs = new URLSearchParams({ from, to });
  return api
    .get<Envelope<VersionDiffResponse>>(`/api/shares/diff?${qs.toString()}`)
    .then(unwrap);
}

// ============================================================================
// Auth-callback share-link bridge — `external-sharing` Phase 5 Session 5.1
// ============================================================================

export interface AuthCallbackShareLinkResponse {
  linkedCount: number;
  primaryShareId: string | null;
}

/**
 * `POST /api/auth/callback/share-link` — post-Supabase-invite hook. Called
 * by `/auth/callback?share=<id>` once `useAuth().user` resolves. Server-side
 * `linkSharesForUser` (Phase 1 Session 1.5) selects all pending shares
 * matching the recipient's email, marks them active, and returns the primary
 * share to redirect to.
 *
 * The body's `shareId` is advisory — server uses email as the canonical
 * lookup key and biases `primaryShareId` toward the share the user clicked
 * (if it's in the linked set). Pass `undefined` when no preferred share is
 * known (e.g. user clicked an old invite link without the `?share=` param).
 */
export function authCallbackShareLink(
  shareId: string | undefined
): Promise<AuthCallbackShareLinkResponse> {
  return api
    .post<Envelope<AuthCallbackShareLinkResponse>>(
      '/api/auth/callback/share-link',
      {
        shareId,
      }
    )
    .then(unwrap);
}

// ============================================================================
// Sandbox-recompute — `external-sharing` Phase 5 Session 5.4
// ============================================================================

/**
 * `POST /api/shared/:shareId/sandbox-recompute` — stateless what-if
 * recompute against the share's frozen_state. Phase 2 Session 2.5 shipped
 * the API; this wrapper serves `useSandboxOverrides` (Phase 5 Session 5.4).
 *
 * Wire contract: server applies the overrides map (keys are namedRanges;
 * server resolves to engine inputs via `engine.setInput(...)`) against a
 * per-share LRU-cached HyperFormula instance, returns the recomputed
 * cell values (`response.values` keyed by `${sheet}!${A1}`) plus any
 * domain errors (`#CIRCULAR_REF`, `#DIV/0`, etc.). 60 req/min rate limit
 * is enforced server-side; 429 surfaces as `ApiError(429)` for the hook
 * to surface in its `transientError` field.
 *
 * The hook owns debouncing (250ms) — this wrapper just fires the POST.
 */
export function sandboxRecompute(
  shareId: string,
  body: SandboxRecomputeRequest
): Promise<SandboxRecomputeResponse> {
  return api
    .post<Envelope<SandboxRecomputeResponse>>(
      `/api/shared/${shareId}/sandbox-recompute`,
      body
    )
    .then(unwrap);
}
